import { AfterViewInit, Component, ElementRef, input, OnDestroy, ViewChild } from '@angular/core';

@Component({
  selector: 'sana-tp-therapist-video-viewer',
  imports: [],
  templateUrl: './therapist-video-viewer.component.html',
  styleUrl: './therapist-video-viewer.component.scss'
})
export class TherapistVideoViewerComponent implements AfterViewInit, OnDestroy {

  @ViewChild('videoEl') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('trackEl') trackRef?: ElementRef<HTMLTrackElement>;

  videoUrl = input.required<string>();
  videoTitle = input.required<string>();
  vttPath = input<string | null>(null);

  isVideoPlaying = false;
  isVideoMuted = true;

  private readonly POSITION_EVENTS = [
    'timeupdate',
    'ratechange',
    'durationchange',
    'seeking',
    'seeked',
    'ended'
  ] as const;

  private cleanupFns: Array<() => void> = [];

  ngAfterViewInit(): void {
    this.initVideoPlayer(this.videoUrl());
  }

  ngOnDestroy(): void {
    for (const off of this.cleanupFns) {
      try {
        off();
      } catch { /* ignore */
      }
    }

    this.cleanupFns = [];
  }

  private async initVideoPlayer(videoUrl: string): Promise<void> {
    const video = this.getVideo();

    this.initMediaSession(video, this.videoTitle());
    this.addVideoEventListeners(video);

    // Track-load fires once the VTT is parsed; only then are cues available
    // for jumpToMarker. Register BEFORE attaching the stream so we never miss it.
    const trackEl = this.trackRef?.nativeElement;
    if (trackEl && this.vttPath()) {
      this.addDomListener(trackEl, 'load', async () => {
        await this.jumpToMarker(2, false);
      });
    } else {
      console.warn('No track element or VTT path provided; jumpToMarker will not work.');
    }

    try {
      await this.attachStream(videoUrl);
    } catch (err) {
      console.error('HLS attach failed:', err);
    }
  }

  private async attachStream(url: string): Promise<void> {
    const video = this.getVideo();

    // Safari/iOS kann HLS nativ
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      return;
    }

    // Andere Browser: hls.js
    const { default: Hls } = await import('hls.js');

    if (Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(url);
      hls.attachMedia(video);
      this.cleanupFns.push(() => hls.destroy());
    } else {
      console.warn('HLS nicht unterstützt und kein Fallback verfügbar.');
    }
  }

  playVideo(): void {
    this.getVideo().play()
      .then(() => {
        this.isVideoPlaying = true;
        this.setMuted(false);
        this.syncMediaSession(this.getVideo());
      })
      .catch(err => console.error('Video play() failed:', err));
  }

  pauseVideo(): void {
    this.getVideo().pause();
    this.isVideoPlaying = false;
    this.syncMediaSession(this.getVideo());
  }

  /** Seeks to marker by index (0 = first). Returns true if successful. */
  public async jumpToMarker(index: number, autoplay = true): Promise<boolean> {
    const trackEl = this.trackRef?.nativeElement;
    const video = this.videoRef?.nativeElement;

    if (!trackEl || !video) {
      return false;
    }

    const track = trackEl.track;
    if (track.mode === 'disabled') track.mode = 'hidden';

    if (!track.cues || track.cues.length === 0) {
      if (trackEl.readyState !== 2) {
        await new Promise<void>((resolve) => {
          const onLoad = () => {
            trackEl.removeEventListener('load', onLoad);
            resolve();
          };
          trackEl.addEventListener('load', onLoad, { once: true });
        });
      }
    }

    const cues = track.cues;
    if (!cues || cues.length === 0) {
      return false;
    }

    if (index < 0 || index >= cues.length) {
      return false;
    }

    const cue = cues[index] as VTTCue;
    const epsilon = 0.01;
    const target = Math.max(0, Math.min((cue.startTime ?? 0) + epsilon, (cue.endTime ?? Infinity) - epsilon));

    try {
      video.pause();
      await Promise.resolve();

      video.currentTime = target;

      await new Promise<void>((resolve, reject) => {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };

        const onError = () => {
          video.removeEventListener('seeked', onSeeked);
          reject();
        };

        video.addEventListener('seeked', onSeeked, { once: true });
        video.addEventListener('error', onError, { once: true });
      });

      if (autoplay) {
        const isAutoplaySuccess = await this.playWithMuteFallback(video);

        if (!isAutoplaySuccess) {
          console.error('[ jumpToMarker ] video.play() failed', index);
        }
      }

      return true;
    } catch {
      console.error('[ jumpToMarker ] Failed to jump to marker index', index);
      return false;
    }
  }

  /**
   * Versucht ein <video> abzuspielen. Bei Autoplay-Block (z.B. iOS/Safari)
   * wird stummgeschaltet und ein zweiter Versuch unternommen.
   */
  private async playWithMuteFallback(video: HTMLVideoElement): Promise<boolean> {
    try {
      await video.play();
      return true;
    } catch (e1) {
      console.warn('video.play() blocked; muting and retrying…', e1);

      this.setMuted(true);

      try {
        await video.play();
        return true;
      } catch (e2) {
        console.error('video.play() failed again after muting.', e2);
        return false;
      }
    }
  }

  private addVideoEventListeners(video: HTMLVideoElement): void {
    const on = <K extends keyof HTMLVideoElementEventMap>(
      type: K,
      handler: (e: HTMLVideoElementEventMap[K]) => void
    ) => this.addDomListener(video, type, handler);

    on('loadedmetadata', () => {
      this.updateMediaSessionPlaybackState(video);
      this.updateMediaSessionPosition(video);
    });

    on('play', () => {
      this.isVideoPlaying = true;
      this.syncMediaSession(video);
    });
    on('pause', () => {
      this.isVideoPlaying = false;
      this.syncMediaSession(video);
    });

    for (const ev of this.POSITION_EVENTS) {
      on(ev as any, () => this.updateMediaSessionPosition(video));
    }
  }

  private initMediaSession(video: HTMLVideoElement, videoName: string): void {
    const ms = this.mediaSession;
    if (!ms) return;

    ms.metadata = new (window as any).MediaMetadata({
      title: `${videoName}`,
      artist: 'sanactivo',
      album: 'Therapieplan',
      artwork: [
        { src: 'https://dev.physio.sanactivo.com/favicon/favicon-96x96.png', sizes: '96x96', type: 'image/png' },
        { src: 'https://dev.physio.sanactivo.com/favicon/web-app-manifest-192x192.png', sizes: '192x192', type: 'image/png' },
        { src: 'https://dev.physio.sanactivo.com/favicon/web-app-manifest-512x512.png', sizes: '512x512', type: 'image/png' }
      ]
    });

    this.setMediaSessionHandlers(video);
  }

  private get mediaSession(): any {
    return (navigator as any).mediaSession ?? null;
  }

  private setMediaSessionHandlers(video: HTMLVideoElement): void {
    const ms = this.mediaSession;
    if (!ms) return;

    ms.setActionHandler?.('play', async () => {
      await video.play();
      this.isVideoPlaying = true;
      this.syncMediaSession(video);
    });

    ms.setActionHandler?.('pause', () => {
      video.pause();
      this.isVideoPlaying = false;
      this.syncMediaSession(video);
    });

    ms.setActionHandler?.('seekforward', (details: any) => {
      const step = details?.seekOffset ?? 10;
      video.currentTime = Math.min(video.currentTime + step, Number.isFinite(video.duration) ? video.duration : video.currentTime + step);
      this.updateMediaSessionPosition(video);
    });

    ms.setActionHandler?.('seekbackward', (details: any) => {
      const step = details?.seekOffset ?? 10;
      video.currentTime = Math.max(video.currentTime - step, 0);
      this.updateMediaSessionPosition(video);
    });

    ms.setActionHandler?.('seekto', (details: any) => {
      const t = details?.seekTime ?? 0;
      if (details?.fastSeek && 'fastSeek' in video) {
        try {
          (video as HTMLVideoElement).fastSeek(t);
        } catch {
          (video as HTMLVideoElement).currentTime = t;
        }
      } else {
        video.currentTime = t;
      }
      this.updateMediaSessionPosition(video);
    });

    ms.setActionHandler?.('stop', () => {
      video.pause();
      video.currentTime = 0;
      this.isVideoPlaying = false;
      this.syncMediaSession(video);
    });
  }

  private updateMediaSessionPlaybackState(video: HTMLVideoElement): void {
    const ms = this.mediaSession;
    if (!ms) return;
    ms.playbackState = video.paused ? 'paused' : 'playing';
  }

  private updateMediaSessionPosition(video: HTMLVideoElement): void {
    const ms = this.mediaSession;
    if (!ms || typeof ms.setPositionState !== 'function') return;
    try {
      ms.setPositionState({
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        playbackRate: video.playbackRate || 1,
        position: Number.isFinite(video.currentTime) ? video.currentTime : 0
      });
    } catch { /* no-op */
    }
  }

  private syncMediaSession(video: HTMLVideoElement): void {
    this.updateMediaSessionPlaybackState(video);
    this.updateMediaSessionPosition(video);
  }

  private getVideo(): HTMLVideoElement {
    return this.videoRef.nativeElement;
  }

  private setMuted(muted: boolean): void {
    const video = this.getVideo();
    video.muted = muted;
    this.isVideoMuted = muted;
  }

  private addDomListener<
    T extends EventTarget,
    K extends string
  >(target: T, type: K, handler: (e: any) => void): void {
    target.addEventListener(type as any, handler as any);
    this.cleanupFns.push(() => target.removeEventListener(type as any, handler as any));
  }
}
